/**
 * Tenant channel feature toggles (order: core messaging surfaces first).
 * API may return a subset; UI lists all known channels for filters and preview.
 */
export const CHANNEL_OPTIONS = [
  'WHATSAPP',
  'EMAIL',
  'INSTAGRAM',
  'MESSENGER',
  'SMS',
  'TELEGRAM',
  'LIVE_CHAT',
  'VOICE',
] as const;

/**
 * Top-level family row plus every `FAMILY:*` provider row currently shown in the matrix.
 * Used for both **deny-all** (parent off → children off) and **allow-all** (parent on → children on).
 */
export function expandFamilyToDescendantCapabilities(
  family: string,
  displayChannels: readonly string[]
): string[] {
  if (family.includes(':')) return [family];
  const prefix = `${family}:`;
  const keys = new Set<string>();
  keys.add(family);
  displayChannels.forEach((c) => {
    if (c.startsWith(prefix)) keys.add(c);
  });
  return [...keys];
}

export function expandFamilyDenyToCapabilities(
  family: string,
  displayChannels: readonly string[]
): string[] {
  return expandFamilyToDescendantCapabilities(family, displayChannels);
}

/** Same keys as deny cascade: turning **allow** on for Email applies to Google/Microsoft/… rows in the list. */
export function expandFamilyAllowToCapabilities(
  family: string,
  displayChannels: readonly string[]
): string[] {
  return expandFamilyToDescendantCapabilities(family, displayChannels);
}

/**
 * Composite provider row plus parent family (`EMAIL` before `EMAIL:GOOGLE`).
 * Used when **allowing** a provider (clear deny up) or **denying** a provider (apply deny up).
 */
export function expandProviderToParentFamily(channel: string): string[] {
  if (!channel.includes(':')) return [channel];
  const i = channel.indexOf(':');
  const family = i > 0 ? channel.slice(0, i) : '';
  if (!family) return [channel];
  return [family, channel];
}

export function expandProviderDenyToParentFamily(channel: string): string[] {
  return expandProviderToParentFamily(channel);
}

/**
 * L2 tenant UI: ordered capability keys from tenant feature flags + role/user access rows only.
 * Derives parent family keys (e.g. EMAIL) when any EMAIL:* appears — no static channel list.
 */
export function buildTenantAdminCapabilityList(
  tenantFlags: readonly { capabilityKey: string }[],
  accessRows: readonly { channel: string }[]
): string[] {
  const keys = new Set<string>();
  tenantFlags.forEach((f) => {
    const k = f.capabilityKey?.trim();
    if (k) keys.add(k);
  });
  accessRows.forEach((r) => {
    const k = r.channel?.trim();
    if (k) keys.add(k);
  });
  [...keys].forEach((k) => {
    const idx = k.indexOf(':');
    if (idx > 0) keys.add(k.slice(0, idx));
  });
  const list = [...keys];
  list.sort((a, b) => {
    const familyOf = (x: string) => {
      const i = x.indexOf(':');
      return i > 0 ? x.slice(0, i) : x;
    };
    const fa = familyOf(a);
    const fb = familyOf(b);
    if (fa !== fb) return fa.localeCompare(fb);
    const aRoot = !a.includes(':');
    const bRoot = !b.includes(':');
    if (aRoot && !bRoot) return -1;
    if (!aRoot && bRoot) return 1;
    return a.localeCompare(b);
  });
  return list;
}
